import { Line } from 'react-chartjs-2';
import React, { useMemo, useRef, useState } from 'react';
import ChartPointDataModal from './ChartPointDataModal';
import { formatRuDate } from '../utils/ruDateUtils';

const ComparisonChart = ({
  loading,
  chartData,
  dataWindow,
  getTableDataByDate,
  getTooltipLines,
  yAxisTitle = 'Значение',
  yMin,
  yMax,
  formatYAxisTick,
  postCards = false,
}) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedTitle, setSelectedTitle] = useState('');
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(null);
  const detailsRequestId = useRef(0);

  // Определяем палитру цветов для датасетов
  const colorPalette = [
    '#1976d2', // Синий
    '#d32f2f', // Красный
    '#388e3c', // Зелёный
    '#f57c00', // Оранжевый
    '#7b1fa2', // Фиолетовый
    '#0288d1', // Светло-синий
    '#c2185b', // Розовый
    '#fbc02d', // Жёлтый
    '#455a64', // Тёмно-серый
    '#0097a7', // Бирюзовый
  ];

  // Определяем метки для маркеров
  const markerLabels = {
    'Среднее по сообществам': 'Среднее по сообществам',
    marker1: 'Маркер 1',
    marker2: 'Маркер 2',
    marker3: 'Маркер 3',
    // Добавьте больше маркеров и их меток, если нужно
  };

  const markerExtents = useMemo(() => {
    const extents = {};
    chartData.forEach(({ marker, data }) => {
      if (Array.isArray(data) && data.length > 0) {
        const values = data
          .map(d => d.value)
          .filter(v => typeof v === 'number' && !isNaN(v));
        if (values.length > 0) {
          const minVal = Math.min(...values);
          const maxVal = Math.max(...values);
          
          // Функция для округления до 0 или 5 на конце, но не меньше исходного значения
          const roundToZeroOrFive = (value) => {
            // Определяем порядок числа
            const order = Math.pow(10, Math.floor(Math.log10(value)));
            const normalized = value / order;
            
            // Округляем до ближайшего числа, оканчивающегося на 0 или 5
            let rounded = Math.ceil(normalized * 1.1);
            if (rounded % 10 === 1 || rounded % 10 === 2) rounded = Math.floor(rounded / 10) * 10 + 5;
            else if (rounded % 10 === 3 || rounded % 10 === 4) rounded = Math.ceil(rounded / 10) * 10;
            else if (rounded % 10 === 6 || rounded % 10 === 7) rounded = Math.floor(rounded / 10) * 10 + 5;
            else if (rounded % 10 === 8 || rounded % 10 === 9) rounded = Math.ceil(rounded / 10) * 10;
            
            const result = rounded * order;
            
            // Убеждаемся, что результат не меньше исходного значения
            return Math.max(result, value);
          };
  
          extents[marker] = {
            min: minVal > 0 ? 0 : Math.floor(minVal * 1.1),
            max: roundToZeroOrFive(maxVal) || 1,
          };
        }
      }
    });
    return extents;
  }, [chartData, dataWindow]);

  // Вычисляем глобальные min и max для оси Y
  const globalExtents = useMemo(() => {
    const allMins = Object.values(markerExtents).map(ext => ext.min);
    const allMaxs = Object.values(markerExtents).map(ext => ext.max);
    const res = {
      min: allMins.length > 0 ? Math.min(...allMins) : 0,
      max: allMaxs.length > 0 ? Math.max(...allMaxs) : 1,
    } 
    return res;
  }, [markerExtents]);

  // Получаем уникальные даты для оси X
  const labels = useMemo(() => {
    if (!chartData || chartData.length === 0) return [];
    
    const allDates = chartData.flatMap(({ data }) => {
      if (!data || data.length === 0) return [];
      // Защищаемся от выхода dataWindow за пределы данных
      const start = Math.max(0, dataWindow.start);
      const end = Math.min(data.length, dataWindow.end);
      return data.slice(start, end).map(d => d.date);
    });
    return [...new Set(allDates)]; // Обеспечиваем уникальность дат
  }, [chartData, dataWindow]);

  // Подписи на оси X для отображения (а не для выборки данных по клику)
  const displayLabels = useMemo(() => labels.map(formatRuDate), [labels]);

  // Формируем датасеты для графика
  const datasets = useMemo(() => {
    if (!chartData || chartData.length === 0) return [];
    
    return chartData.map(({ marker, groupId, topicLabel, topicTitle, data: seriesData }, index) => {
      if (!seriesData || seriesData.length === 0) {
        return {
          label: markerLabels[marker] || marker,
          groupId,
          topicLabel,
          topicTitle,
          data: [],
          fill: false,
          borderColor: marker === 'Среднее по сообществам' ? '#000000' : colorPalette[index % colorPalette.length],
          backgroundColor: marker === 'Среднее по сообществам' ? '#000000' : colorPalette[index % colorPalette.length],
          tension: 0.2,
          spanGaps: true,
          clip: false,
        };
      }
      
      // Защищаемся от выхода dataWindow за пределы данных
      const start = Math.max(0, dataWindow.start);
      const end = Math.min(seriesData.length, dataWindow.end);
      const windowedData = seriesData.slice(start, end);
      
      // Если маркер равен 0, используем чёрный цвет, иначе берём цвет из палитры
      const color = marker === 'Среднее по сообществам' ? '#000000' : colorPalette[index % colorPalette.length];
      return {
        label: markerLabels[marker] || marker, // Используем метку из markerLabels или сам маркер
        groupId,
        topicLabel,
        topicTitle,
        data: windowedData.map(d => d.value),
        sourceData: windowedData,
        fill: false,
        borderColor: color,
        backgroundColor: color,
        tension: 0.2,
        spanGaps: true,
        clip: false,
      };
    });
  }, [chartData, dataWindow]);

  // Функция для создания подписи временного окна
  const getDataWindowLabel = () => {
    if (labels.length === 0) return 'Показаны данные: нет данных';
    return `Показаны данные: ${displayLabels[0]} - ${displayLabels[displayLabels.length - 1]}`;
  };

  // Обработчик клика по точке на графике
  const handlePointClick = async (date, series) => {
    if (!getTableDataByDate) return;
    const requestId = ++detailsRequestId.current;
    setSelectedDate(date);
    setSelectedTitle(series.topicTitle ? `${series.topicTitle} · ${series.marker}` : series.marker);
    setSelectedRows([]);
    setDetailsError(null);
    setDetailsLoading(true);
    try {
      const rows = await getTableDataByDate(date, series);
      if (requestId === detailsRequestId.current) setSelectedRows(rows || []);
    } catch (err) {
      console.error('Ошибка при получении данных по дате:', err);
      if (requestId === detailsRequestId.current) {
        setSelectedRows([]);
        setDetailsError(postCards ? 'Не удалось загрузить посты за этот период' : 'Не удалось загрузить данные за этот период');
      }
    } finally {
      if (requestId === detailsRequestId.current) setDetailsLoading(false);
    }
  };

  return (
    <>
      <div style={{ height: '50px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        {loading ? (
          <div style={{
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #3498db',
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            animation: 'spin 1s linear infinite'
          }} />
        ) : (
          <p style={{ textAlign: 'center', fontWeight: 'bold', color: '#169af7' }}>
            {getDataWindowLabel()}
          </p>
        )}

        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>

      <div style={{ width: "100%", marginBottom: 40 }}>
        <h3 style={{ textAlign: 'center', marginBottom: 12 }}>Комбинированные данные</h3>
        <div style={{ height: '300px', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }} >
        <Line
          data={{
            labels: displayLabels,
            datasets,
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            plugins: {
              legend: { display: true }, // Показываем легенду для различия линий
              title: { display: false },
              ...(getTooltipLines ? {
                tooltip: {
                  callbacks: {
                    label: (context) => {
                      const point = context.dataset.sourceData?.[context.dataIndex];
                      return getTooltipLines({
                        label: context.dataset.label,
                        point,
                        value: context.parsed.y,
                      });
                    },
                  },
                },
              } : {}),
            },
            interaction: { mode: 'nearest', intersect: false },
            onClick: (evt, elements) => {
              if (!elements.length || !getTableDataByDate) return;
              const { index, datasetIndex } = elements[0];
              const date = labels[index]; // исходный ISO-ключ для загрузки данных
              const series = datasets[datasetIndex];
              if (date && series) handlePointClick(date, {
                marker: series.label,
                groupId: series.groupId,
                topicLabel: series.topicLabel,
                topicTitle: series.topicTitle,
              });
            },
            scales: {
              x: { title: { display: true, text: 'Дата' } },
              y: {
                title: { display: true, text: yAxisTitle },
                min: yMin ?? globalExtents.min,
                max: yMax ?? globalExtents.max,
                ...(formatYAxisTick ? {
                  ticks: { callback: formatYAxisTick },
                } : {}),
              },
            },
          }}
        />
        </div>
      </div>

      {/* Модальное окно с данными по выбранной дате */}
      <ChartPointDataModal
        date={selectedDate}
        rows={selectedRows}
        loading={detailsLoading}
        error={detailsError}
        postCards={postCards}
        title={selectedTitle}
        onClose={() => { detailsRequestId.current += 1; setSelectedDate(null); }}
      />
    </>
  );
};

export default ComparisonChart;
